"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Clarity from "@microsoft/clarity";
import { useAuthStore } from "@/store/useAuthStore";
import { isRealVisitor, pageTypeOf, tagValue } from "@/lib/analytics/clarity-tags";

// Empty when the env var is unset — the component then does nothing at all,
// so this can ship before the Clarity project actually exists.
const PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID?.trim() || "";
function clarityIsLive() {
  return typeof (window as unknown as {
    clarity?: unknown;
  }).clarity === "function";
}

/**
 * Microsoft Clarity (session replay + heatmaps) loader.
 *
 * Renders nothing; it exists only to run `Clarity.init` once on the client.
 * The package injects <script src="https://www.clarity.ms/tag/..."> into the
 * DOM itself, which is why no nonce is passed here and none is needed: the
 * CSP in src/proxy.ts uses 'strict-dynamic', so a script inserted by an
 * already-trusted (nonce'd Next bundle) script inherits that trust. Adding it
 * as an inline <Script> like the analyt.ir snippet in [lang]/layout.tsx would
 * work too, but the official package keeps the tag version updatable through
 * pnpm instead of a hand-pasted minified blob.
 *
 * PRIVACY. Nothing here masks anything — masking is a project-level setting in
 * the Clarity dashboard and it is set to Strict for this project. That is
 * deliberate and it is the only safe choice for this app: enumerating every
 * DOM node that shows a wallet address, a balance, a bid amount or a phone
 * number across the deposit/withdraw/swap/redeem/settings surfaces is a
 * default-allow design, and one missed node leaks financial data into a
 * recording. Strict is default-deny. The one code-level mask that still
 * exists (input-otp.tsx) is defence in depth for the single highest-value
 * secret, in case that dashboard setting is ever relaxed to Balanced.
 *
 * For the same reason the tags below are deliberately coarse — a locale, a
 * theme, whether someone is signed in, the kind of page and whether it is the
 * installed app. `Clarity.identify` is NOT called: it would ship a user
 * identifier to a third party for no gain that these filters do not provide.
 *
 * REAL VISITORS ONLY (`isRealVisitor`): a local production build and the
 * speed-test tools and crawlers that run a real browser were about half the
 * recorded sessions. And WHY THE PAGE SHIFTS: the lab never shows the home
 * page's field CLS (0 in Lighthouse, 0.36 for real phones), so web-vitals'
 * attribution build names the element that moved, per session, as a tag.
 */
export default function ClarityAnalytics({
  lang
}: {
  lang: string;
}) {
  const [live, setLive] = useState(false);
  // Only the signed-in/guest distinction is read, never the identity itself,
  // so the effect below does not re-run on every unrelated profile change.
  const isSignedIn = useAuthStore(state => Boolean(state.user));
  const pathname = usePathname();
  const taggedRef = useRef<string>("");
  useEffect(() => {
    if (!PROJECT_ID) return;

    // Local dev sessions would otherwise land in the same recording pool as
    // real users and skew the "where do people rage-click" signal the tool is
    // being added for. Same opt-in shape as NEXT_PUBLIC_ENABLE_PWA_DEV.
    const devOptIn = process.env.NEXT_PUBLIC_ENABLE_CLARITY_DEV === "true";
    if (process.env.NODE_ENV !== "production" && !devOptIn) return;
    if (!devOptIn && !isRealVisitor({
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      webdriver: navigator.webdriver === true,
      userAgent: navigator.userAgent
    })) return;

    // Deferred to idle rather than fired straight from this effect: the effect
    // runs during hydration, so an immediate init makes the browser fetch and
    // execute a third-party tag while it is still competing for the main
    // thread with the app's own hydration work — paid for in INP on exactly
    // the low-end mobile sessions this tool is meant to observe. The tag only
    // needs to be running before the user does anything interesting, not
    // before first paint. requestIdleCallback is absent on Safari <16.4, hence
    // the setTimeout fallback.
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      Clarity.init(PROJECT_ID);
      setLive(true);
    };
    const idle = "requestIdleCallback" in window;
    const handle = idle ? window.requestIdleCallback(start, {
      timeout: 4000
    }) : window.setTimeout(start, 2000);
    return () => {
      cancelled = true;
      if (idle) window.cancelIdleCallback(handle as number);else window.clearTimeout(handle as number);
    };
  }, []);
  useEffect(() => {
    if (!live || !clarityIsLive()) return;

    // Theme is read off the root class rather than next-themes: this component
    // is mounted above ThemeProvider in [lang]/layout.tsx, and the `.dark`
    // class is the actual source of truth the whole design system keys off
    // (base.css, v2.css), so there is nothing to gain from moving it inside.
    const theme = document.documentElement.classList.contains("dark") ? "dark" : "light";
    const auth = isSignedIn ? "signed-in" : "guest";
    const signature = `${lang}|${theme}|${auth}`;
    // The auth tag arrives twice on a signed-in load — once as "guest" before
    // the manual auth-store rehydration in ClientLayout finishes, then again
    // for real. Re-sending identical tags on unrelated re-renders is what this
    // guards against; the genuine guest → signed-in correction still goes out.
    if (taggedRef.current === signature) return;
    taggedRef.current = signature;
    Clarity.setTag("locale", lang);
    Clarity.setTag("theme", theme);
    Clarity.setTag("auth", auth);
  }, [live, lang, isSignedIn]);

  // Once per session: the installed app or a browser tab. The manifest's
  // start_url carries ?source=pwa; display-mode covers a later navigation.
  useEffect(() => {
    if (!live || !clarityIsLive()) return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & {
      standalone?: boolean;
    }).standalone === true || new URLSearchParams(window.location.search).get("source") === "pwa";
    Clarity.setTag("app_mode", standalone ? "pwa" : "browser");
  }, [live]);

  // Every client-side route change: a session's tags then list every kind of
  // page it visited, so "sessions that reached the wallet" is one filter.
  useEffect(() => {
    if (!live || !clarityIsLive()) return;
    Clarity.setTag("page_type", pageTypeOf(pathname));
  }, [live, pathname]);

  // The element behind each Core Web Vital, read where the lab cannot see it.
  // Loaded only once Clarity is live, so no visitor it ignores pays for it;
  // the observers are buffered, so shifts before this point still count.
  useEffect(() => {
    if (!live || !clarityIsLive()) return;
    let cancelled_0 = false;
    import("web-vitals/attribution").then(({
      onCLS,
      onINP,
      onLCP
    }) => {
      if (cancelled_0) return;
      onCLS(({
        value,
        rating,
        attribution
      }) => {
        Clarity.setTag("cls", rating);
        Clarity.setTag("cls_value", value.toFixed(2));
        if (attribution.largestShiftTarget) Clarity.setTag("cls_target", tagValue(attribution.largestShiftTarget));
        if (rating === "poor") Clarity.event("cls_poor");
      });
      onINP(({
        rating: rating_0,
        attribution: attribution_0
      }) => {
        Clarity.setTag("inp", rating_0);
        if (attribution_0.interactionTarget) Clarity.setTag("inp_target", tagValue(attribution_0.interactionTarget));
      });
      onLCP(({
        rating: rating_1,
        attribution: attribution_1
      }) => {
        Clarity.setTag("lcp", rating_1);
        if (attribution_1.target) Clarity.setTag("lcp_target", tagValue(attribution_1.target));
      });
    }).catch(() => {});
    return () => {
      cancelled_0 = true;
    };
  }, [live]);
  return null;
}